import { Container, InputField, SubmitButton } from '@/components/Auth/Buttons';
import { StyledLink } from '@/components/Auth/Links';
import { useAuth } from '@/providers/AuthProvider';
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

/**
 * Interface for the request reset password input
 */
interface RequestResetInput {
  username: string;
}

/**
 * RequestResetPassword component
 *
 * This component renders a form that allows users to request a password reset.
 * It handles the input of the user's email (used as username), sends a reset request,
 * and navigates to the reset password page upon successful request.
 */
export default function RequestResetPassword() {
  // State for managing loading status, form input, and error messages
  const [loading, setLoading] = useState(false);
  const [requestInput, setRequestInput] = useState<RequestResetInput>({ username: '' });
  const [error, setError] = useState<string | undefined>();

  // Hooks for authentication and navigation
  const auth = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isMigrated = (searchParams.get('migrated') ?? '').toLowerCase() === 'true';

  useEffect(() => {
    const username = searchParams.get('username');
    if (username) {
      setRequestInput({ username });
    }
  }, [searchParams]);

  /**
   * Handles changes in the input field
   * @param {React.ChangeEvent<HTMLInputElement>} e - The input change event
   */
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setError(undefined);
    const { name, value } = e.target;
    setRequestInput((prev) => ({ ...prev, [name]: value }));
  };

  /**
   * Handles the password reset request submission
   * @param {React.FormEvent} event - The form submission event
   */
  const requestReset = async (event: React.FormEvent) => {
    event.preventDefault();
    setLoading(true);

    try {
      const normalizedUsername = requestInput.username.trim().toLowerCase();
      await auth.resetPassword({ username: normalizedUsername });
      navigate(`/auth/reset?username=${normalizedUsername}`);
    } catch (err: any) {
      setError(err.message);
      console.error('Error during password reset request:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container
      title="Set Your New Password"
      subtitle={
        isMigrated
          ? "Scribl's moved to a new platform - with great new features coming soon! " +
            "We've already moved your account, and you just need to reset your password to login."
          : 'Enter your email to request a password reset.'
      }
      banner={error}
      className="relative md:w-600 px-20 py-32 md:px-100 md:py-32 rounded-[12px] bg-white backdrop-blur-xs shadow-xs z-1"
    >
      <form className="w-full flex flex-col space-y-12" onSubmit={requestReset}>
        <InputField
          name="username"
          type="email"
          label="Email Address"
          value={requestInput.username}
          onChange={handleInputChange}
        />
        <SubmitButton title="Request Reset" loading={loading} onSubmit={requestReset} />
      </form>
      <div className="mt-16">
        <StyledLink label="Remember your password?" linkText="Back to Sign In" to="/auth/sign-in" />
      </div>
    </Container>
  );
}
